import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { FeatureKey } from 'common/constants/permission'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { useInvalidateCostQueries } from 'modules/cost/api/invalidate'
import { useGetPondNameById, useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import MoreAction from 'modules/pond/components/MoreAction'
import { useMutateDeleteShrimpTopup } from 'modules/shrimpTopup/api/mutation'
import { useGetShrimpTopupById } from 'modules/shrimpTopup/api/queries'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Skeleton,
} from '@mui/material'

import { normalizeSpeciesName } from 'utils/analytics'

import ShrimpTopupRecordDialog from '../ShrimpTopupRecordDialog'
import ShrimpTopupRecordBodyTopupSkeleton from '../ShrimpTopupSkeleton'

interface ShrimpTopupViewDialogProps {
  open: boolean
  onClose: () => void
  shrimpTopupId: string
}

const ShrimpTopupViewDialog = ({ open, onClose, shrimpTopupId }: ShrimpTopupViewDialogProps) => {
  const { t } = useTranslation('shrimpTopup')
  const { pondId } = useParams()
  const { data: pondInfo } = useGetPondNameById(pondId || '')
  const species = useSpeciesName()
  const isFish = useIsFishSpecie()
  const { data: shrimpTopupData, isLoading, refetch } = useGetShrimpTopupById(shrimpTopupId)
  const [deletingId, setDeletingId] = useState<string>('')
  const [isEditMode, setIsEditMode] = useState(false)
  const deleteMutation = useMutateDeleteShrimpTopup()
  const invalidateCostQueries = useInvalidateCostQueries()
  const { trackEvent } = useAnalytics()
  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      await deleteMutation.mutateAsync(deletingId)
      invalidateCostQueries()
      onClose()
      const normalizedSpecies = normalizeSpeciesName(species)
      trackEvent(
        AnalyticsEvents.stockingRecordDeleted,
        normalizedSpecies ? { species: normalizedSpecies } : undefined
      )
    },
  })

  const handleOpenDeleteConfirm = (id: string) => {
    setDeletingId(id)
    openConfirmDialog()
  }

  const handleOpenEdit = () => {
    setIsEditMode(true)
  }

  const handleCloseEdit = () => {
    setIsEditMode(false)
    refetch()
  }

  const handleClose = () => {
    setIsEditMode(false)
    onClose()
  }

  return (
    <>
      {dialog}
      <ShrimpTopupRecordDialog
        onClose={handleCloseEdit}
        open={isEditMode}
        editingTopupId={shrimpTopupId}
        initialData={shrimpTopupData}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 0 },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={700} fontSize={20} color="primary.main">
              {isLoading ? (
                <Skeleton width={200} />
              ) : (
                <>
                  {t('topup.record')}
                  {species} {t('topup.pond')} {pondInfo?.name}
                </>
              )}
            </Typography>
            {!isLoading && shrimpTopupData && (
              <MoreAction
                featureKey={FeatureKey.ShrimpTopup}
                onClickEdit={handleOpenEdit}
                onClickDelete={() => handleOpenDeleteConfirm(shrimpTopupId)}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          {isLoading ? (
            <ShrimpTopupRecordBodyTopupSkeleton />
          ) : !shrimpTopupData ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Typography color="text.secondary">
                {t('topup.noDataAvailable')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography fontWeight={600} fontSize={16}>
                  {formatThaiShortDateTwoDigitYear(shrimpTopupData.topupDate ?? '', {
                    showTime: true,
                  })}
                </Typography>
                <Typography fontWeight={600} fontSize={16}>
                  {t('topup.shrimpAge', { species })} {shrimpTopupData.shrimpAge} {t('topup.day')}
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography color="text.secondary" fontWeight={500} fontSize={16}>
                    {t('topup.water')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontSize={15}>{t('topup.salinity')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={500} fontSize={16} textAlign="right">
                    {shrimpTopupData.salinity}{' '}
                    <span style={{ color: '#B0BEC5', fontWeight: 400 }}>(ppt)</span>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography color="text.secondary" fontWeight={500} fontSize={16}>
                    {species}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontSize={15}>{t('topup.breedCondition', { species })}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={500} fontSize={16} textAlign="right">
                    {shrimpTopupData.breedName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontSize={15}>{t('topup.brandName', { species })}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={500} fontSize={16} textAlign="right">
                    {shrimpTopupData.brandName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontSize={15}>{t('topup.breedProductName', { species })}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={500} fontSize={16} textAlign="right">
                    {shrimpTopupData.breedProductName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontSize={15}>
                    {t('topup.initialShrimpCount', { species })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={500} fontSize={16} textAlign="right">
                    {shrimpTopupData.shrimpCount?.toLocaleString()}{' '}
                    <span style={{ color: '#B0BEC5', fontWeight: 400 }}>
                      {t('topup.additionalReleaseCount', { species })}
                    </span>
                  </Typography>
                </Grid>
                {isFish && (
                  <>
                    <Grid item xs={6}>
                      <Typography fontSize={15}>{t('topup.averageSize')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography fontWeight={500} fontSize={16} textAlign="right">
                        {shrimpTopupData.averageSize != null
                          ? shrimpTopupData.averageSize.toLocaleString()
                          : '-'}{' '}
                        <span style={{ color: '#B0BEC5', fontWeight: 400 }}>
                          {t('topup.gramPerOne')}
                        </span>
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={handleClose}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {t('topup.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ShrimpTopupViewDialog
